<template>
  <div class="add-many-contacts-wrapper">
    <div class="action-modal-wrapper" v-if="showModal">
      <div class="action-modal-container">
        <div class="confirmation-wrapper" v-if="didUserAgreeToTerms !== true">
          <div class="scrollable-terms">
            <div class="confirmation-header">
              <strong>Almost there!</strong><br />
              Before uploading, please read and attest to the following:
            </div>
            <div class="confirmation-list">
              <div class="list-item">
                <div class="header">1. I have permission to email these people</div>
                <div class="body">
                  Building a quality email list takes time, patience, and a verifiable opt-in process. Getting people's
                  permission to send them marketing emails not only complies with anti-spam regulations (violating these
                  can result in steep fines for your business), but it's good for your brand and helps you avoid
                  damaging your domain reputation.
                </div>
              </div>
              <div class="list-item">
                <div class="header">2. This is not a third party list</div>
                <div class="body">
                  Third-party lists of email addresses are prohibited from use with {{ platformTitle }}. This includes
                  purchased or rented lists, and lists scraped from third-party sources including public websites. Any
                  contacts you add to {{ platformTitle }} must have been collected entirely by you. While purchasing a
                  list is not technically illegal, most reputable email service providers prohibit sending to purchased
                  lists.
                </div>
              </div>
              <div class="list-item">
                <div class="header">3. Uploading a "junk" list has consequences</div>
                <div class="body">
                  If you upload and send to a list with a high number of undeliverable email addresses or spam reports,
                  your domain reputation could be severely damaged and your {{ platformTitle }} account may be
                  temporarily or permanently suspended. Not trying to be mean here. We just want you to be the best
                  marketer you can be.
                </div>
              </div>
            </div>
          </div>
          <div class="terms-agreement-wrapper">
            <div class="terms-checkbox">
              <input type="checkbox" id="terms-checkbox" data-test="terms-checkbox" v-model="didUserAgreeToTerms" />
            </div>
            <div class="terms-body-wrapper">
              <div class="body">I understand and agree</div>
            </div>
          </div>
          <div class="terms-action-wrapper">
            <div class="actionButton" @click="closeModal()" data-test="cancel-button">Cancel</div>
            <div class="actionButton" @click="showImportOptions()" data-test="next-button">Next</div>
          </div>
          <div class="terms-error-message" v-if="didUserAgreeToTerms === false" data-test="terms-error">
            Please agree to the above terms before proceeding to import contacts.
          </div>
        </div>
        <div class="m-body" v-if="didUserAgreeToTerms">
          <div class="import-contacts-header">
            <strong>Add Many Contacts</strong><br />
            <small>Please select where you would like to add contacts from</small>
          </div>
          <div class="col-sm-12 text-center">
            <div v-if="selectImportMethod" class="center-block inline-block">
              <a :href="selectMethodUrl" class="actionButton"> Select Import Method </a>
            </div>
            <template v-else>
              <div class="center-block inline-block">
                <import-contacts
                  v-if="!disableCsvImportOption && (processing == null || processing === 'csv-import')"
                  :post-url="csvImportPostUrl"
                  :redirect-url="redirectUrl"
                  :vessel-id="vesselId"
                  @close-modal="onCloseModal()"
                  @action-processing="onActionProcessing"
                >
                </import-contacts>
              </div>
              <div class="center-block inline-block">
                <select-and-import-groups-from-redtail
                  v-if="redtailStatus && (processing == null || processing === 'redtail-import')"
                  :redirect-url="redirectUrl"
                  :vessel-id="vesselId"
                  :campaign-id="campaignId"
                  @action-processing="onActionProcessing"
                >
                </select-and-import-groups-from-redtail>
              </div>
              <div class="center-block inline-block">
                <salesforce-import-groups
                  v-if="salesforceStatus && (processing == null || processing === 'salesforce-import')"
                  :vessel-id="vesselId"
                  :redirect-url="redirectUrl"
                  :campaign-id="campaignId"
                  @action-processing="onActionProcessing"
                >
                </salesforce-import-groups>
              </div>
            </template>
          </div>
        </div>
        <div class="close-modal" @click="closeModal()">x</div>
      </div>
    </div>
    <span>
      <button @click="handleShowModal()" :class="buttonClass" data-test="add-many-contacts-button" ref="trigger">
        {{ buttonText }}
      </button>
    </span>
  </div>
</template>

<script>
import ImportContacts from '../components/ImportContacts';
import SelectAndImportGroupsFromRedtail from '../components/SelectAndImportGroupsFromRedtail';
import SalesforceImportGroups from '../components/SalesforceImportGroups';
import ActionModal from '../components/ActionModal';

export default {
  name: 'add-many-contacts',
  methods: {
    showImportOptions() {
      this.didUserAgreeToTerms = document.getElementById('terms-checkbox').checked;
    },
    handleShowModal() {
      this.showModal = true;
      this.headerElem.style.display = 'none';
    },
    closeModal() {
      this.showModal = false;
      this.headerElem.style.display = 'block';
    },
    onCloseModal() {
      this.closeModal();
    },
    onActionProcessing(data) {
      this.processing = data.type;
    },
  },
  computed: {
    csvImportPostUrl: function () {
      let url = '/api/?entity=contacts&action=handleCsvImport';

      if (!!this.campaignId) {
        url = '/api/?entity=contacts&action=handleCsvImportToCampaign&campaignId=' + this.campaignId;
      } else if (!!this.groupId) {
        url = '/api/?entity=contacts&action=handleCsvImportToAudience&audienceId=' + this.groupId;
      }

      return url;
    },
    selectMethodUrl() {
      let url = `/import/select-method/${window.location.search}&`;
      if (!!this.campaignId) {
        url = url + `campaignId=${this.campaignId}`;
      } else if (!!this.groupId) {
        url = url + `groupId=${this.groupId}`;
      }
      return url;
    },
  },
  props: {
    vesselId: {
      type: Number,
      required: true,
    },
    redirectUrl: {
      type: String,
      required: true,
    },
    redtailStatus: {
      type: Boolean,
      required: false,
    },
    salesforceStatus: {
      type: Boolean,
      required: false,
    },
    campaignId: {
      type: Number,
      required: false,
    },
    groupId: {
      type: [Number, String],
      required: false,
    },
    buttonText: {
      type: String,
      required: false,
      default: 'Add Many Contacts',
    },
    disableCsvImportOption: {
      type: Boolean,
      require: false,
      default: false,
    },
    buttonClass: {
      type: String,
      default: 'actionButton addManyContactsButton',
    },
    platformTitle: {
      type: String,
      default: 'Snappy Kraken',
    },
    selectImportMethod: {
      type: Boolean,
      default: false,
    },
    showAgreement: {
      type: Boolean,
      default: true,
    },
  },
  components: {ImportContacts, ActionModal, SelectAndImportGroupsFromRedtail, SalesforceImportGroups},
  data() {
    return {
      headerElem: document.getElementById('masthead'),
      showModal: false,
      didUserAgreeToTerms: null,
      processing: null,
    };
  },
  mounted() {
    this.didUserAgreeToTerms = !this.showAgreement;
  },
};
</script>

<style lang="scss">
.add-many-contacts-wrapper {
  display: inline;
}

.action-modal-wrapper {
  background: rgb(102, 204, 204);
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1000000;
  height: 100%;
  width: 100%;
}

.action-modal-container {
  margin: 0 auto;
  max-width: 960px;
  width: 100%;
  height: 100%;
  padding: 100px 0 0 0;
}

.close-modal {
  position: absolute;
  top: 0;
  right: 35px;
  color: #777;
  font-size: 50px;
  font-weight: bold;
  cursor: pointer;
  transition: 350ms ease all;
}

.close-modal:hover {
  color: #333;
}

.action-modal-button {
  display: block;
  margin-right: 10px;
  padding: 10px 25px;
  transition: 350ms ease all;
}

.m-body {
  text-align: center;

  .import-contacts-header {
    font-size: 33px;
    color: #fff;
    padding: 0 0 30px 0;
  }
}

.confirmation-wrapper {
  color: #fff;

  .scrollable-terms {
    height: 450px;
    overflow-y: scroll;
    padding: 0 0 30px 0;

    .confirmation-header {
      font-size: 37px;
    }

    .confirmation-list {
      .list-item {
        padding: 15px 0;

        .header {
          font-size: 29px;
        }

        .body {
          font-size: 21px;
        }
      }
    }
  }

  .terms-agreement-wrapper {
    padding: 15px 0;

    .terms-checkbox {
      display: inline-block;

      #terms-checkbox {
        display: block;
        margin-top: -14px;
        -ms-transform: scale(2);
        -moz-transform: scale(2);
        -webkit-transform: scale(2);
        -o-transform: scale(2);
      }
    }

    .terms-body-wrapper {
      font-size: 28px;
      display: inline-block;
      padding: 0 0 0 10px;
    }
  }

  .terms-action-wrapper {
    float: left;
    width: 100%;
    text-align: center;
    padding: 15px 0;

    .actionButton {
      display: inline;
    }
  }

  .terms-error-message {
    font-size: 22px;
    color: red;
  }
}

.inline-block {
  display: inline-block;
}
</style>
